// Address-bar navigation, history, reload. Gates proxied src on navReady.
import { state, byId, active } from "./state.js";
import * as quests from "./quests.js";
import { setAddr } from "./dom.js";
import { encodeFor, navReady, transportFor } from "./proxy.js";
import { render, frames } from "./view.js";
import { startWatch, engineLoadFailed, leaving } from "./errors.js";
import { get } from "../shared/settings-store.js";
import { SEARCH_TEMPLATES } from "../shared/settings-schema.js";
import { signInWall, signInWallText } from "./signin-walls.js";
import { newTab } from "./tabs.js";
import { isLinuxTab, leaveLinuxOk } from "./linux-open.js";

function searchUrl(q) {
	const eng = get("searchEngine") || "duckduckgo";
	const tpl = eng === "custom" ? get("customSearch") || SEARCH_TEMPLATES.duckduckgo : SEARCH_TEMPLATES[eng] || SEARCH_TEMPLATES.duckduckgo;
	return tpl.indexOf("%s") !== -1 ? tpl.replace("%s", encodeURIComponent(q)) : tpl + encodeURIComponent(q);
}

// True when the typed value should be treated as an address rather than a search
// query (a scheme, or a dotted host with no spaces). Shared with the autocomplete.
export function looksUrl(v) {
	v = (v || "").trim();
	if (!v) return false;
	if (/^[a-z]+:\/\//i.test(v)) return true;
	return /^[^\s.]+\.[^\s]{2,}/.test(v) && !/\s/.test(v);
}

export function norm(v) {
	v = (v || "").trim();
	if (!v) return "";
	if (/^https?:\/\//i.test(v)) return v;
	// Any OTHER explicit scheme (file:, gopher:, ftp:, data:, javascript:, …) must
	// never reach the proxy engine - treat it as a search query instead.
	if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return searchUrl(v);
	if (looksUrl(v)) return "https://" + v;
	return searchUrl(v);
}

export function navigate(raw) { navigateTab(state.activeId, raw); }

export function navigateTab(id, raw, opts) {
	const url = norm(raw);
	if (!url) return;
	quests.track("search");
	const tab = byId(id);
	if (!tab) return;
	// Typing a URL/search while on the Linux tab opens it in a new tab: replacing the Linux page
	// would stop its machine (and lose anything since its last save).
	if (isLinuxTab(tab)) return navigateTab(newTab(), raw, opts);
	// Any other first-party page (Settings holding a carry code's last open) asks before it's replaced.
	if (tab.internal && !leaveLinuxOk(tab)) return;
	// Providers that refuse to authenticate through a proxy get a notice INSTEAD of a
	// load, so nobody types a password into a page that was never going to accept it.
	// Never a hard block: the notice carries a "Try anyway" that comes back with force.
	const wall = opts && opts.force ? null : signInWall(url);
	const title = url.replace(/^[a-z]+:\/\//i, "").split("/")[0] || url;
	leaving(tab);
	if (get("incognito")) tab.priv = true; // never saved for "Restore tabs"
	else {
		tab.hist = tab.hist.slice(0, tab.hi + 1);
		tab.hist.push({ url, src: "" });
		tab.hi = tab.hist.length - 1;
	}
	tab.url = url; tab.addr = url; tab.view = null; tab.internal = null; tab.src = ""; tab.loading = true; tab.focusOnLoad = true; tab.skipLoader = false; tab.title = title; tab.error = null; tab.favicon = null; tab.autoSwapped = false; tab.wait = null;
	if (id === state.activeId) setAddr(url);
	if (wall) {
		const copy = signInWallText(wall);
		tab.loading = false;
		tab.error = { title: copy.title, message: copy.message, kind: "signin", url };
		render();
		return;
	}
	render();
	// Per-tab state from an automatic transport/engine switch belongs to the previous page.
	tab.triedTransports = null; tab.triedEngines = null;
	navReady()
		.then(() => transportFor(url)) // put the saved connection back unless this site needs another
		.then(() => (tab.url === url ? encodeFor(url, tab) : null))
		.then((src) => {
			if (src == null || tab.url !== url) return; // moved on to a different URL
			tab.src = src;
			if (tab.hist[tab.hi]) { tab.hist[tab.hi].src = tab.src; tab.hist[tab.hi].engine = tab.engine; }
			startWatch(id);
			render();
		})
		.catch(() => { if (tab.url === url) engineLoadFailed(id); });
}

function step(dir) {
	const t = active();
	if (!t) return;
	const ni = t.hi + dir;
	if (ni < 0 || ni >= t.hist.length) return;
	if (t.internal && !leaveLinuxOk(t)) return; // the page shown now would be swapped out
	const h = t.hist[ni];
	leaving(t);
	t.hi = ni; t.url = h.url; t.addr = h.url; t.view = null; t.internal = null; t.error = null; t.loading = true; t.skipLoader = false; t.favicon = null; t.wait = null;
	setAddr(h.url);
	if (h.src) {
		t.src = h.src;
		if (h.engine) t.engine = h.engine;
		startWatch(t.id);
		render();
	} else {
		// Not yet encoded (navigated during the boot window) - gate on navReady so we
		// never set a raw, un-proxied URL while the engine is still loading.
		t.src = "";
		render();
		navReady()
			.then(() => encodeFor(h.url, t))
			.then((src) => {
				if (t.hi !== ni || t.url !== h.url) return; // moved on
				t.src = src;
				h.src = t.src; h.engine = t.engine;
				startWatch(t.id);
				render();
			})
			.catch(() => { if (t.hi === ni) engineLoadFailed(t.id); });
	}
}
export function back() { step(-1); }
export function forward() { step(1); }

export function reload() {
	const t = active();
	if (!t) return;
	const f = frames.get(t.id);
	if (t.internal) { if (f && leaveLinuxOk(t)) f.src = f.getAttribute("src"); return; }
	if (!t.src || !f) return;
	t.loading = true; t.error = null; t.favicon = null;
	startWatch(t.id);
	render();
	if (f.rvRemote) { f.reload(); return; } // sandbox mode: the page lives on S
	try { f.contentWindow.location.reload(); } catch (e) { f.src = f.getAttribute("src"); }
}
